import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { IFlatModel } from '@services/api/flat/flat-model.interface';
import { IParkingRequestModel } from '@services/api/parking-request/parking-request-model.interface';
import { format } from 'date-fns';
import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

/**
 * Service to handle form data mapping for parking request forms
 */
@Injectable({
  providedIn: 'root'
})
export class ParkingRequestFormMappingService {
  /**
   * Map form values to parking request model for API submission
   */
  mapFormToModel(formValue: any, parkingRequestId?: string): IParkingRequestModel {
    const model: IParkingRequestModel = {
      // Required by II11Model
      id: parkingRequestId ? parseInt(parkingRequestId, 10) : 0,
      '@id': parkingRequestId ? `/api/parking_requests/${parkingRequestId}` : '',

      // Required fields
      booking: formValue.booking, // Now stores IRI directly
      start_date: formValue.start_date ? format(new Date(formValue.start_date), 'yyyy-MM-dd') : '',
      end_date: formValue.end_date ? format(new Date(formValue.end_date), 'yyyy-MM-dd') : '',

      // Optional fields
      parkingPlace: formValue.parkingPlace || null, // Now stores IRI directly
      disabled_parking_place: formValue.disabled_parking_place || false,
      license_plate_not_known: formValue.license_plate_not_known || false,
      license_plate: formValue.license_plate_not_known ? null : formValue.license_plate || null,
      license_plate_country_code: formValue.license_plate_not_known ? null : formValue.license_plate_country_code || null,
      paid: formValue.paid || false
    };

    return model;
  }

  /**
   * Map parking request model to form values for editing
   */
  mapModelToForm(parkingRequest: IParkingRequestModel, form: FormGroup | null, component?: any): void {
    if (!parkingRequest) {
      return;
    }

    // Process dates
    const startDate = parkingRequest.start_date ? new Date(parkingRequest.start_date) : null;
    const endDate = parkingRequest.end_date ? new Date(parkingRequest.end_date) : null;

    // Set form values from the parking request model
    const formValues = {
      // booking will be loaded separately
      start_date: startDate,
      end_date: endDate,
      // parkingPlace will be loaded separately
      disabled_parking_place: parkingRequest.disabled_parking_place || false,
      license_plate_not_known: parkingRequest.license_plate_not_known || false,
      license_plate: parkingRequest.license_plate || '',
      license_plate_country_code: parkingRequest.license_plate_country_code || '',
      paid: parkingRequest.paid || false
    };

    // Only patch reactive form if it exists (for backward compatibility)
    if (form) {
      form.patchValue(formValues);
    }

    // Sync component state variables for template-driven form display (if component provided)
    if (component) {
      // Date fields
      component.startDateValue = startDate;
      component.endDateValue = endDate;

      // Checkbox fields
      component.disabledParkingPlaceValue = parkingRequest.disabled_parking_place || false;
      component.paidValue = parkingRequest.paid || false;
      component.licensePlateNotKnownValue = parkingRequest.license_plate_not_known || false;

      // License plate fields
      component.licensePlateValue = parkingRequest.license_plate || '';
      component.licensePlateCountryCodeValue = parkingRequest.license_plate_country_code || null;
    }
  }

  /**
   * Apply initial values from container component inputs
   */
  applyInitialValues(
    form: FormGroup,
    initialBookingId?: string | null,
    initialParkingPlaceId?: string | null,
    initialStartDate?: Date | null,
    bookingModel?: IBookingModel | null,
    parkingPlaceModel?: IFlatModel | null
  ): void {
    if (!form) return;

    const formControls = form.controls;

    // Set initial booking if provided
    if (bookingModel) {
      formControls['booking'].setValue(bookingModel);
    }

    // Set initial parking place if provided
    if (parkingPlaceModel) {
      formControls['parkingPlace'].setValue(parkingPlaceModel);
    }

    // Set initial start date if provided
    if (initialStartDate) {
      formControls['start_date'].setValue(initialStartDate);
    }
  }

  /**
   * Reset form to initial state
   */
  resetForm(form: FormGroup): void {
    if (!form) return;

    form.reset({
      booking: null,
      start_date: null,
      end_date: null,
      parkingPlace: null,
      disabled_parking_place: false,
      license_plate_not_known: false,
      license_plate: '',
      license_plate_country_code: null,
      paid: false
    });
  }

  /**
   * Extract numeric ID from IRI string
   */
  private extractIdFromIri(iri: string): number {
    const parts = iri.split('/');
    const idString = parts[parts.length - 1];
    return parseInt(idString, 10) || 0;
  }
}
